import React, { Fragment } from "react";

import { format } from "date-fns";
import { fullDateFullYearFormat } from "../../../../common/helpers/date";
import {
    realReportDataEnd,
    realReportDataStart,
} from "../../../../common/helpers/reports";
import { AccountingReportFilters } from "../../types";
import { AccountingReportFooter } from "../../AccountingReportFooter";
import { Entity } from "../../../../common/types/entity";
import { TrialBalanceResponse } from "../../../../common/contracts/accounting-reports";
import { TrialBalanceDownloadCell } from "./TrialBalanceDownloadCell";
import { TrialBalanceDownloadHeader } from "./TrialBalanceDownloadHeader";
import { TrialBalanceDownloadTitle } from "./TrialBalanceDownloadTitle";

interface Props {
    report: TrialBalanceResponse;
    filters: AccountingReportFilters;
    generationDate: Date;
    entity: Entity;
}

export const TRIAL_BALANCE_DOWNLOAD_REPORT_TABLE_ID =
    "trial-balance-download-table";

const TABLE_PROPS: React.HTMLAttributes<HTMLTableElement> = {
    id: TRIAL_BALANCE_DOWNLOAD_REPORT_TABLE_ID,
};

export const TrialBalanceDownloadTable: React.FC<Props> = ({
    report,
    filters,
    generationDate,
    entity,
}) => {
    const { rows, periods } = report;

    const startDate = format(
        realReportDataStart(periods[0].dateStart, filters.startDate),
        fullDateFullYearFormat,
    );
    const endDate = format(
        realReportDataEnd(periods[periods.length - 1].dateEnd, filters.endDate),
        fullDateFullYearFormat,
    );
    const entityName = entity.name;
    const columnsNumber = 2 * periods.length + 3;

    const emptyRow = (
        <tr>
            <td colSpan={columnsNumber}>&nbsp;</td>
        </tr>
    );

    return (
        <table className="download-table" {...TABLE_PROPS}>
            <tbody>
                {emptyRow}
                <tr>
                    <td
                        colSpan={columnsNumber}
                        className={
                            "download-table__cell--bold download-table__cell--center"
                        }
                        style={{ fontSize: "22px" }}
                    >
                        {entityName}
                    </td>
                </tr>
                <tr>
                    <td
                        colSpan={columnsNumber}
                        className={
                            "download-table__cell--bold download-table__cell--center"
                        }
                        style={{ fontSize: "22px" }}
                    >
                        Trial Balance
                    </td>
                </tr>
                <tr>
                    <td
                        colSpan={columnsNumber}
                        className="download-table__cell--center"
                    >
                        {startDate} - {endDate}
                    </td>
                </tr>
                {emptyRow}
                <tr>
                    <td />
                    <td />
                    {periods.map(({ label }) => (
                        <TrialBalanceDownloadHeader colSpan={2} key={label}>
                            {label}
                        </TrialBalanceDownloadHeader>
                    ))}
                </tr>
                <tr>
                    <td />
                    <td />
                    {periods.map(({ label }) => (
                        <Fragment key={label}>
                            <TrialBalanceDownloadHeader>
                                Debit
                            </TrialBalanceDownloadHeader>
                            <TrialBalanceDownloadHeader>
                                Credit
                            </TrialBalanceDownloadHeader>
                        </Fragment>
                    ))}
                </tr>
                {rows.map((row, index) => (
                    <Fragment key={row.key}>
                        <tr>
                            <td className="download-table__spacing_col" />
                            <TrialBalanceDownloadTitle title={row.label} />
                            {row.values.map((value, innerIndex) => (
                                <TrialBalanceDownloadCell
                                    key={
                                        periods[Math.floor(innerIndex / 2)]
                                            .label
                                    }
                                    isSummary={index === rows.length - 1}
                                    value={value}
                                />
                            ))}
                        </tr>
                    </Fragment>
                ))}
                {emptyRow}
                {emptyRow}
                <tr>
                    <td
                        colSpan={columnsNumber}
                        className={
                            "download-table__cell--center download-table__font-9"
                        }
                    >
                        <AccountingReportFooter
                            generationDate={generationDate}
                        />
                    </td>
                </tr>
            </tbody>
        </table>
    );
};
